import { DEVICE_ALERTS_EVENTS, HUB_DASHBOARD_QUERY_PARAM } from '@/constants/device';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { updateNotificationMiniBanner } from '@vantiva-sa/homesight_humaui/Store/notificationStore';
import { unzipMessage } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  CallBusyStatus,
  DefaultLanguage,
  NavigationKeys,
  WSMessageTypes,
  WSNotificationTypes,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  showContactNotification,
  showInsightAlertNotification,
  showScheduleNotification,
} from '../Helpers/DesktopNotificationHelper';
import {
  handleCallStatusUpdate,
  handleVideoCallBusy,
  handleVideoCallClose,
  handleVideoCallReceive,
} from '../Helpers/VideoCallHelper';
import { refreshModulesForMessage } from './notificationRefresh';

export const processMessage = ({
  dispatch,
  message,
  sendMessage,
  meetingInfo,
  videoCallModalVisible,
  videoCallNotificationModalVisible,
  currentUser,
  resourceMap,
  navigationRef,
  queryClient,
}) => {
  try {
    const { payload } = message;
    const origMessage = unzipMessage(payload);
    const jsonMessage = JSON.parse(origMessage);
    const messageType = jsonMessage?.message_type;
    const messageObj = jsonMessage[messageType];
    /* Data refresh must not depend on whether a desktop notification is shown below. */
    refreshModulesForMessage(queryClient, messageType, messageObj);
    if (messageType === WSMessageTypes.videoCallStatus) {
      handleCallStatusUpdate({ sendMessage, dispatch, message: messageObj });
    } else if (messageType === WSMessageTypes.notification) {
      const { notification_type } = messageObj;
      if (notification_type === WSNotificationTypes.videoCall) {
        handleVideoCallNotification({
          messageObj,
          meetingInfo,
          videoCallModalVisible,
          videoCallNotificationModalVisible,
          dispatch,
          sendMessage,
        });
      } else {
        console.warn('No handler for notification type:', notification_type);
      }
    } else if (messageType === WSMessageTypes.calendar) {
      if (
        messageObj.schedule_event?.action === 'delete' ||
        messageObj.schedule_event?.action === 'modified' ||
        messageObj.schedule_event?.action === 'deleteall'
      )
        return;
      showScheduleNotification(jsonMessage.calendar);
      dispatch(
        updateNotificationMiniBanner({
          message_type: WSMessageTypes.calendar,
          message: jsonMessage.calendar,
        })
      );
    } else if (messageType === WSMessageTypes.contact) {
      if (
        messageObj?.action === 'deleted' ||
        messageObj?.action === 'delete' ||
        messageObj?.action?.toLowerCase() === 'modified' ||
        jsonMessage?.contact?.useruuid === currentUser?.useruuid
      )
        return;
      showContactNotification(jsonMessage.contact, currentUser, resourceMap);
      dispatch(
        updateNotificationMiniBanner({
          message_type: WSMessageTypes.contact,
          message: jsonMessage.contact,
        })
      );
    } else if (Object.values(DEVICE_ALERTS_EVENTS).includes(messageType)) {
      showInsightAlertNotification(jsonMessage, () =>
        openAccountDashboard(jsonMessage?.principal_useruuid, navigationRef)
      );
    } else {
      console.warn('No message handler for type:', messageType);
    }
  } catch (error) {
    console.error('Error processing websocket message:', error);
  }
};

const openAccountDashboard = (principalUserUuid, navigationRef) => {
  const { navigate, handleMainMenuClick, navigationGuard } = navigationRef?.current ?? {};
  if (!principalUserUuid || typeof navigate !== 'function') return;

  const proceed = () => {
    /* Keep existing params - KeycloakService resolves realm/org from the URL on reload. */
    const search = new URLSearchParams(window.location.search);
    search.set(HUB_DASHBOARD_QUERY_PARAM, principalUserUuid);
    navigate(`/${NavigationKeys.accounts}?${search.toString()}`);
    handleMainMenuClick?.(NavigationKeys.accounts);
  };
  navigationGuard ? navigationGuard(proceed) : proceed();
};

const handleVideoCallNotification = ({
  messageObj,
  meetingInfo,
  videoCallModalVisible,
  videoCallNotificationModalVisible,
  dispatch,
  sendMessage,
}) => {
  const { meeting_id, callStatus } = meetingInfo ?? {};
  if (CallBusyStatus.includes(callStatus) || videoCallNotificationModalVisible) {
    handleVideoCallBusy({ message: messageObj, sendMessage });
  } else {
    if (videoCallModalVisible) {
      handleVideoCallClose({ meeting_id, sendMessage, dispatch });
    }
    handleVideoCallReceive({ dispatch, message: messageObj, sendMessage });
  }
};

export const checkForMaintenance = async (userInfo, setAnnouncement, maintenanceApi, trans) => {
  const errorCallback = (error) => {
    console.error('Error fetching maintenance announcement:', error);
  };
  const successCallback = (data) => {
    if (!data || !Object.keys(data).length) {
      return setAnnouncement({ type: null, message: null });
    }

    const isAnnouncement = Boolean(data.announcement?.message);
    const announcement = {
      type: isAnnouncement ? 'Announcement' : 'Information',
      message: isAnnouncement ? data.announcement.message : data.information?.message,
    };

    setAnnouncement(announcement);
  };
  try {
    const locale = userInfo?.locale || DefaultLanguage;
    maintenanceApi.getAnnouncementByLocale(
      locale,
      apiCallback({
        translator: trans,
        failCallback: errorCallback,
        errorCallback,
        successCallback,
      })
    );
  } catch (error) {
    errorCallback(error);
  }
};
